'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useSession, signOut } from 'next-auth/react'
import { useDisconnect } from 'wagmi'
import Image from 'next/image'
import WalletConnect from './WalletConnect'
import ThemeToggle from './ThemeToggle'
import { Avatar } from './ui/Avatar'
import { Button, LinkButton } from './ui/Button'

export default function Nav() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { data: session } = useSession()
  const { disconnect } = useDisconnect()
  const navRef = useRef<HTMLElement>(null)
  const hamburgerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close mobile menu on outside tap or Escape key
  useEffect(() => {
    if (!menuOpen) return
    const onMouse = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        hamburgerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onMouse)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouse)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setMenuOpen(false)
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <nav
      ref={navRef}
      data-component="Nav"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between transition-all duration-300
        ${
          scrolled
            ? 'px-4 sm:px-6 md:px-10 py-2.5 bg-white/95 dark:bg-dark/95 shadow-[0_4px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_30px_rgba(0,0,0,0.3)]'
            : 'px-4 sm:px-6 md:px-10 py-3 md:py-4 bg-white/85 dark:bg-dark/85'
        }
        backdrop-blur-xl border-b border-carolina/20 dark:border-carolina/[0.08]`}
    >
      <Link data-part="logo" href="/" className="shrink-0" aria-label="TrueLORE home">
        <Image
          src="/brand/truelore-lockup-horizontal-light.svg"
          alt="TrueLORE"
          width={120}
          height={32}
          className="h-14 lg:h-12 xl:h-14 w-auto dark:hidden"
          priority
        />
        <Image
          src="/brand/truelore-lockup-horizontal-dark.svg"
          alt="TrueLORE"
          width={120}
          height={32}
          className="h-14 lg:h-12 xl:h-14 w-auto hidden dark:block"
          priority
        />
      </Link>

      {/* Desktop links */}
      <div data-part="desktop-links" className="hidden lg:flex items-center lg:gap-3 xl:gap-6">
        <Link
          data-part="nav-link-ledger"
          href="/ledger"
          className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
        >
          LEDGER
        </Link>
        <Link
          data-part="nav-link-verify"
          href="/verify"
          className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
        >
          VERIFY
        </Link>
        <Link
          data-part="nav-link-plugins"
          href="/plugins"
          className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
        >
          PLUGIN
        </Link>
        <Link
          data-part="nav-link-features"
          href="/#features"
          className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
        >
          FEATURES
        </Link>
        <a
          data-part="nav-link-why-lore"
          href="https://true-lore.com"
          className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
        >
          WHY TrueLORE
        </a>
        {session && (
          <Link
            data-part="nav-link-stream"
            href="/stream"
            className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
          >
            STREAM
          </Link>
        )}
        {session && (
          <Link
            data-part="nav-link-my-streams"
            href="/my-streams"
            className="text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] font-medium transition-colors"
          >
            MY STREAMS
          </Link>
        )}
        <ThemeToggle />
        <WalletConnect />
        {session ? (
          <div data-part="user-menu" className="flex items-center gap-3">
            {/* Always rendered: it is the only way to /profile on desktop, and
                an email + password account has no picture to show. */}
            <Link
              href="/profile"
              data-part="user-avatar-link"
              aria-label="Profile"
              className="group rounded-full"
            >
              <Avatar
                src={session.user?.image}
                name={session.user?.name}
                email={session.user?.email}
                size={32}
                className="border border-carolina/30 group-hover:border-carolina transition-colors"
              />
            </Link>
            <button
              data-part="sign-out-btn"
              onClick={() => {
                disconnect()
                signOut({ callbackUrl: '/' })
              }}
              className="text-[13px] tracking-[1px] xl:text-[16px] xl:tracking-[2px] text-steel dark:text-cream/60 hover:text-carolina-dark dark:hover:text-carolina transition-colors font-mono"
            >
              SIGN OUT
            </button>
          </div>
        ) : (
          // Goes to the auth page rather than straight to a provider: the
          // user picks Google or email + password there, and whether they are
          // signing in or creating an account. /auth/signin's own default
          // callback is /auth/verify, which is where this used to land.
          <LinkButton
            data-part="sign-in-btn"
            variant="secondary"
            href="/auth/signin"
            className="text-[14px] xl:text-[16px]"
          >
            SIGN IN
          </LinkButton>
        )}
      </div>

      {/* Mobile hamburger */}
      <button
        ref={hamburgerRef}
        data-part="mobile-hamburger"
        className="lg:hidden flex flex-col justify-center gap-[5px] w-8 h-8"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
        aria-expanded={menuOpen}
        aria-controls="mobile-menu"
      >
        <span
          className={`block w-6 h-0.5 bg-ink dark:bg-cream transition-all duration-300 origin-center ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}
        />
        <span
          className={`block w-6 h-0.5 bg-ink dark:bg-cream transition-all duration-300 ${menuOpen ? 'opacity-0 scale-x-0' : ''}`}
        />
        <span
          className={`block w-6 h-0.5 bg-ink dark:bg-cream transition-all duration-300 origin-center ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}
        />
      </button>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          id="mobile-menu"
          data-part="mobile-menu"
          className="absolute top-full left-0 right-0 bg-white dark:bg-dark border-b border-carolina/20 dark:border-carolina/10 flex flex-col p-5 gap-1 lg:hidden shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
        >
          <Link
            data-part="mobile-link-ledger"
            href="/ledger"
            onClick={() => setMenuOpen(false)}
            className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
          >
            LEDGER
          </Link>
          <Link
            data-part="mobile-link-verify"
            href="/verify"
            onClick={() => setMenuOpen(false)}
            className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
          >
            VERIFY
          </Link>
          <Link
            data-part="mobile-link-plugins"
            href="/plugins"
            onClick={() => setMenuOpen(false)}
            className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
          >
            PLUGIN
          </Link>
          <Link
            data-part="mobile-link-features"
            href="/#features"
            onClick={() => setMenuOpen(false)}
            className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
          >
            FEATURES
          </Link>
          <a
            data-part="mobile-link-why-lore"
            href="https://true-lore.com"
            onClick={() => setMenuOpen(false)}
            className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
          >
            WHY TrueLORE
          </a>
          {session && (
            <Link
              data-part="mobile-link-stream"
              href="/stream"
              onClick={() => setMenuOpen(false)}
              className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
            >
              STREAM
            </Link>
          )}
          {session && (
            <Link
              data-part="mobile-link-my-streams"
              href="/my-streams"
              onClick={() => setMenuOpen(false)}
              className="py-3 px-2 text-ink dark:text-cream hover:text-carolina-dark dark:hover:text-carolina text-[16px] tracking-[2px] font-medium transition-colors border-b border-ink/[0.06] dark:border-white/[0.04]"
            >
              MY STREAMS
            </Link>
          )}
          <div data-part="mobile-auth" className="pt-3 flex flex-col gap-3">
            {session && (
              <Link
                href="/profile"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-1 group"
              >
                <Avatar
                  src={session.user?.image}
                  name={session.user?.name}
                  email={session.user?.email}
                  size={24}
                  className="border border-carolina/30 group-hover:border-carolina transition-colors"
                />
                <span className="text-[16px] text-steel dark:text-cream/60 group-hover:text-carolina-dark dark:group-hover:text-carolina font-mono truncate transition-colors">
                  {session.user?.name ?? session.user?.email}
                </span>
              </Link>
            )}

            <div data-part="mobile-action-row" className="flex gap-3 items-center">
              <ThemeToggle />
              <WalletConnect fluid />
              {session ? (
                <Button
                  data-part="mobile-sign-out-btn"
                  variant="secondary"
                  onClick={() => {
                    disconnect()
                    signOut({ callbackUrl: '/' })
                    setMenuOpen(false)
                  }}
                  className="flex-1"
                >
                  SIGN OUT
                </Button>
              ) : (
                <LinkButton
                  data-part="mobile-sign-in-btn"
                  variant="secondary"
                  href="/auth/signin"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 text-center"
                >
                  SIGN IN
                </LinkButton>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
